import { useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { StepIndicator } from "./indicator";
import { useNavigate } from "react-router-dom";
import { Navigation } from "./navigation";
import { useSources, useDestinations, useCreateConnection, AirbyteConnector } from "../../../views/marketing-pages/connectors/connector/airbyte-api";

// A connection joins two *already-created* source/destination connectors
// (POST /{org}/sources, POST /{org}/destinations — see sources.tsx/
// destinations.tsx) — real GET /{org}/sources and /{org}/destinations here,
// not the connector-type catalog those pages pick from. Replaces the old
// 4-step wizard's fake "Configure Source" form (a second, disconnected copy
// of SetupGuide's fields) and its `alert("Connection created successfully!")`
// with a real POST /{org}/connections, then an optional real first sync.

const ConnectorPickRow: React.FC<{ item: AirbyteConnector; idField: "sourceId" | "destinationId"; selected: boolean; onSelect: () => void }> = ({
  item,
  idField,
  selected,
  onSelect,
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-left transition-colors ${
      selected
        ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-500/10"
        : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
    }`}
  >
    <span>
      <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{item.name}</span>
      <span className="block text-xs text-gray-400 dark:text-gray-500">{(item as any)[idField === "sourceId" ? "sourceType" : "destinationType"]}</span>
    </span>
    {selected && <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">Selected</span>}
  </button>
);

export default function NewConnectionFlow() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSource, setSelectedSource] = useState<AirbyteConnector | null>(null);
  const [selectedDestination, setSelectedDestination] = useState<AirbyteConnector | null>(null);
  const [connectionName, setConnectionName] = useState("");

  const { data: sources, loading: sourcesLoading } = useSources(true);
  const { data: destinations, loading: destinationsLoading } = useDestinations(true);
  const { create: createConnection, loading: creating } = useCreateConnection();

  const steps = ["Select Source", "Select Destination", "Review & Create"];

  const nextStep = (): void => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };
  const prevStep = (): void => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!selectedSource;
      case 2:
        return !!selectedDestination;
      case 3:
        return connectionName.trim().length > 0;
      default:
        return false;
    }
  };

  // Per direct ask: once the connection is actually created, go straight to
  // the Connections list (where it now shows up, and where "Sync Now" lives)
  // rather than lingering on this wizard with its own separate inline sync
  // button — one place for sync status instead of two.
  const handleSubmit = async () => {
    if (!selectedSource?.sourceId || !selectedDestination?.destinationId) return;
    const { connection, error } = await createConnection({
      name: connectionName.trim(),
      sourceId: selectedSource.sourceId,
      destinationId: selectedDestination.destinationId,
    });
    if (connection) {
      toast.success(`${connectionName} connected.`);
      navigate("/track/connections");
    } else {
      toast.error(error || "Couldn't create this connection.");
    }
  };

  const handleBackNavigation = (): void => {
    if (currentStep === 1) window.history.back();
    else prevStep();
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="w-full">
        <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
            <button onClick={() => navigate("/track/connections")}>Connections</button>
            <span>/</span>
            <span>New connection</span>
          </div>
          <StepIndicator currentStep={currentStep} steps={steps} />
        </div>

        <div className="mx-auto px-6 py-8 max-w-3xl">
          {currentStep === 1 && (
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Select Source</h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Pick one of your already-connected sources.</p>
              {sourcesLoading ? (
                <p className="flex items-center gap-1.5 text-sm text-gray-400">
                  <Loader2 size={14} className="animate-spin" /> Loading your sources…
                </p>
              ) : sources.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No sources connected yet.</p>
                  <button onClick={() => navigate("/track/sources")} className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                    Connect a source first →
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {sources.map((s) => (
                    <ConnectorPickRow key={s.sourceId} item={s} idField="sourceId" selected={selectedSource?.sourceId === s.sourceId} onSelect={() => setSelectedSource(s)} />
                  ))}
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Select Destination</h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Pick one of your already-connected destinations.</p>
              {destinationsLoading ? (
                <p className="flex items-center gap-1.5 text-sm text-gray-400">
                  <Loader2 size={14} className="animate-spin" /> Loading your destinations…
                </p>
              ) : destinations.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No destinations connected yet — none have a working provider on this workspace yet.</p>
                  <button onClick={() => navigate("/track/destinations")} className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                    View destinations →
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {destinations.map((d) => (
                    <ConnectorPickRow
                      key={d.destinationId}
                      item={d}
                      idField="destinationId"
                      selected={selectedDestination?.destinationId === d.destinationId}
                      onSelect={() => setSelectedDestination(d)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Review & Create</h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">This runs manually until you set up a schedule.</p>

              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-4">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{selectedSource?.name}</span>
                  <ArrowRight size={16} className="text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">{selectedDestination?.name}</span>
                </div>
              </div>

              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Connection Name</label>
                <input
                  value={connectionName}
                  onChange={(e) => setConnectionName(e.target.value)}
                  placeholder={`${selectedSource?.name} to ${selectedDestination?.name}`}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          <Navigation
            currentStep={currentStep}
            onBack={handleBackNavigation}
            onContinue={nextStep}
            onSubmit={handleSubmit}
            canProceed={canProceed()}
            isLastStep={currentStep === 3}
            submitting={creating}
          />
        </div>
      </div>
    </div>
  );
}
